/* eslint-disable @typescript-eslint/naming-convention */
import { useEffect, useState } from 'react';
import Icon from 'threads5/Icon';
import { animated, easings, useSpringValue } from '@react-spring/web';

import { RichTextSwitch } from 'src/containers';
import { useCmsMode } from 'src/contexts/ModeContext/ModeContext';
import { useComponentTheme } from 'src/hooks/useComponentTheme';
import ThemeProvider from 'threads5/themes/theme-provider';

import Section from 'src/components-v2/Layout/Section/Section';
import Container from 'src/components-v2/Layout/Container/Container';
import Row from 'src/components-v2/Layout/Grid/Row';
import Column from 'src/components-v2/Layout/Grid/Column';
import { TextStack } from './TextStack';
import {
  AccordionDetails,
  AccordionDetailsContent,
  AccordionDetailsSummary,
  Strip,
  AccordionIcons,
} from './Accordion.styled';
import { AccordionProps } from './Accordion.types';
import CmsMediaSwitch from 'src/components-v2/Media/CmsMediaSwitch/CmsMediaSwitch';
import Stack from 'src/components-v2/Layout/Stack/Stack';
import { makeImageQueryString, makeImageSource } from 'src/lib/utils';
import CmsSectionalHeader from 'src/sections/Header/CmsSectionalHeader/CmsSectionalHeader';

const COLUMN_CONFIG = {
  'Asset left / Text right': {
    left: {
      xs: 22,
      xsOffset: 1,
      sm: 12,
      smOffset: 0,
      md: 10,
      mdOffset: 2,
    },
    right: {
      xs: 22,
      xsOffset: 1,
      sm: 12,
      smOffset: 0,
      md: 9,
      mdOffset: 1,
    },
  },
  'Text left / Asset right': {
    left: {
      xs: 22,
      xsOffset: 1,
      sm: 12,
      smOffset: 0,
      md: 10,
      mdOffset: 1,
    },
    right: {
      xs: 22,
      xsOffset: 1,
      sm: 12,
      smOffset: 0,
      md: 9,
      mdOffset: 2,
    },
  },
};

const AnimatedStack = animated(Stack);

const MediaColumn = ({ sys, fields, layout, activeIndex }) => {
  const asset = fields?.accordionItems?.[activeIndex]?.fields?.asset?.fields;
  const { smImgSrc, mdImgSrc, lgImgSrc } = makeImageSource(asset);

  const opacity = useSpringValue(0, {
    config: {
      duration: 300,
      easing: easings.easeInOutBack,
    },
  });

  useEffect(() => {
    opacity.set(0);
    opacity.start(1);
  }, [activeIndex, opacity]);

  return (
    <Column
      {...COLUMN_CONFIG[layout].left}
      sx={{ display: { xs: 'none', sm: 'flex' } }}
    >
      <AnimatedStack
        direction='column'
        // top-align the asset on tablet, vertically center on desktop
        justifyContent={{ sm: 'flex-start', md: 'center' }}
        sx={(theme) => {
          return {
            height: '100%',
            maxWidth: '100%',
            // a CMS width/height override on a video/Lottie asset must scale to
            // the column, not overflow into the text column on smaller screens
            '& > *': { maxWidth: '100%' },
            // react-lazyload wraps the asset in a .lazyload-wrapper that is
            // forced to height:100% (fills the column), so the asset's own
            // alignment must mirror the Stack: top on tablet, centered on desktop
            '& .lazyload-wrapper': {
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              [theme.breakpoints.up('md')]: {
                justifyContent: 'center',
              },
            },
            img: { width: '100%', height: 'auto', borderRadius: '8px' },
            video: {
              display: 'block',
              width: '100%',
              height: 'auto',
              borderRadius: '8px',
            },
          };
        }}
        style={{ opacity }}
      >
        <CmsMediaSwitch
          key={asset?.sys?.id + activeIndex}
          asset={asset}
          sys={sys}
          queryString={{ xl: '?fm=webp&w=1354&q=60' }}
          sources={{
            xs: makeImageQueryString({ src: smImgSrc, width: 562 }),
            sm: makeImageQueryString({ src: smImgSrc, width: 468 }),
            md: makeImageQueryString({ src: mdImgSrc, width: 490 }),
            lg: makeImageQueryString({ src: lgImgSrc, width: 557 }),
            xl: makeImageQueryString({ src: lgImgSrc, width: 677 }),
          }}
          width={asset?.width}
          height={asset?.height}
          sx={{ borderRadius: '8px' }}
        />
      </AnimatedStack>
    </Column>
  );
};

const TextColumn = ({ sys, fields, layout, handleClick, activeIndex }) => {
  return (
    <Column
      {...COLUMN_CONFIG[layout].right}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
      }}
    >
      <TextStack sys={sys} fields={{ body: fields?.body }} />
      {fields?.accordionItems?.map((item, index) => {
        const asset = item?.fields?.asset?.fields;
        const { smImgSrc, mdImgSrc, lgImgSrc } = makeImageSource(asset);

        return (
          <AccordionDetails
            key={item.sys.id}
            name={sys?.id}
            open={index === activeIndex}
          >
            <AccordionDetailsSummary
              onClick={(e) => {
                e.preventDefault();
                handleClick(index);
              }}
            >
              <h3>{item.fields.heading}</h3>
              <AccordionIcons>
                <Icon
                  className='accordion-icon-chevron'
                  name='ChevronDown'
                  size='20'
                />
              </AccordionIcons>
            </AccordionDetailsSummary>
            <AccordionDetailsContent>
              <RichTextSwitch
                content={item.fields.body}
                sys={item.sys}
                nodes={{
                  p: {
                    variant: 'P2 Regular',
                    component: 'p',
                    fieldId: 'body',
                  },
                }}
              />
              <CmsMediaSwitch
                key={asset?.sys?.id}
                asset={asset}
                sys={sys}
                queryString={{ xl: '?fm=webp&w=1354&q=60' }}
                sources={{
                  xs: makeImageQueryString({ src: smImgSrc, width: 562 }),
                  sm: makeImageQueryString({ src: smImgSrc, width: 468 }),
                  md: makeImageQueryString({ src: mdImgSrc, width: 490 }),
                  lg: makeImageQueryString({ src: lgImgSrc, width: 557 }),
                  xl: makeImageQueryString({ src: lgImgSrc, width: 677 }),
                }}
                width={asset?.width}
                height={asset?.height}
                sx={{
                  display: { xs: 'block', sm: 'none' },
                  maxWidth: '100%',
                  video: { maxWidth: '100%', height: 'auto' },
                }}
              />
            </AccordionDetailsContent>
            <Strip />
          </AccordionDetails>
        );
      })}
    </Column>
  );
};

const ColumnSwitch = ({
  sys,
  fields,
  layout,
  handleClick,
  activeTextIndex,
  activeMediaIndex,
}) => {
  switch (layout) {
    case 'Asset left / Text right':
      return (
        <>
          <MediaColumn
            sys={sys}
            fields={fields}
            layout={layout}
            activeIndex={activeMediaIndex}
          />
          <TextColumn
            sys={sys}
            fields={fields}
            layout={layout}
            handleClick={handleClick}
            activeIndex={activeTextIndex}
          />
        </>
      );
    case 'Text left / Asset right':
      return (
        <>
          <TextColumn
            sys={sys}
            fields={fields}
            layout={layout}
            handleClick={handleClick}
            activeIndex={activeTextIndex}
          />
          <MediaColumn
            sys={sys}
            fields={fields}
            layout={layout}
            activeIndex={activeMediaIndex}
          />
        </>
      );
    default:
      return (
        <>
          <MediaColumn
            sys={sys}
            fields={fields}
            layout={layout}
            activeIndex={activeMediaIndex}
          />
          <TextColumn
            sys={sys}
            fields={fields}
            layout={layout}
            handleClick={handleClick}
            activeIndex={activeTextIndex}
          />
        </>
      );
  }
};

const Accordion: React.FC<AccordionProps> = ({ fields, sys }) => {
  const [activeTextIndex, setActiveTextIndex] = useState<number | null>(0);
  const [activeMediaIndex, setActiveMediaIndex] = useState<number>(0);

  const mode = useCmsMode({
    mode: fields?.mode,
    backgroundColor: fields?.backgroundColor,
  });
  const componentTheme = useComponentTheme(mode);
  const layout = fields?.layout;
  const background =
    componentTheme.palette[fields?.backgroundColor] || fields?.backgroundColor;
  const isGradient = fields?.backgroundColor?.includes('gradient');

  const handleClick = (index: number) => {
    // toggle active accordion but keep displaying active media
    if (index === activeTextIndex) {
      setActiveTextIndex(null);
      return;
    }
    setActiveTextIndex(index);
    setActiveMediaIndex(index);
  };

  return (
    <ThemeProvider theme={componentTheme}>
      <Section sx={{ background, ...fields?.styles }}>
        {fields?.header ? (
          <CmsSectionalHeader
            fields={{
              ...fields?.header?.fields,
              backgroundColor: isGradient ? 'transparent' : background,
              mode,
            }}
            sys={fields?.header?.sys}
          />
        ) : null}
        <Container>
          <Row>
            <ColumnSwitch
              sys={sys}
              fields={fields}
              layout={layout}
              handleClick={handleClick}
              activeTextIndex={activeTextIndex}
              activeMediaIndex={activeMediaIndex}
            />
          </Row>
        </Container>
      </Section>
    </ThemeProvider>
  );
};

export default Accordion;
